import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	useTheme,
	Grid,
	Tooltip,
	Typography
} from '@material-ui/core';

import Constants from "../utils/Constants";
import NumberFormatter from '../utils/NumberFormatter';

const styles = makeStyles(theme => ({
	pointer: {
		cursor: "pointer"
	},

	container: {
		width: "auto"
	},

	item: {
		display: "flex",
		paddingLeft: theme.spacing(1.5)
	},

	circle: {
		width: 28,
		height: 28,
		display: "flex",
		alignItems: "center",
		justifyContent: "center"
	},

	number: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
		color: theme.palette.secondary.slate
	},

	tightPackItem: {
		paddingLeft: theme.spacing(1)
	},

	densePackItem: {
		paddingLeft: theme.spacing(.7518796992481203)
	},

	tooltip: {
		//marginLeft: theme.spacing(-1)
	},

	hidden: {
		display: "none"
	},

	textIndicator: {
		fontSize: "0.9rem",
		whiteSpace: "nowrap",
		marginLeft: theme.spacing(1)
	}
}));

function UserActivityIndicators(props) {
	const theme = useTheme();

	const getIcon = (action, theme) => {
		let config = Constants.ACTIVITY.AUDIT_TRAIL[action.toUpperCase()];
		return <FontAwesomeIcon icon={["fas", config.ICON]} color={theme.palette.secondary.slate} title={config.ID} />; //color={config.COLOR}
	};

	const getTooltip = (tip) => {
		if (!props.disableTooltips) {
			if (Array.isArray(tip)) {
				let user,
					cnt = 1,
					max = 5,
					content = [];

				for(var item of tip) {
					if (cnt <= max) {
						user = item.user || item.sender || {name: item.user_name};
						content.push(<div key={`key-${cnt}`}>{user.name}</div>);
						cnt++;
					} else {
						content.push(<div key={`key-${cnt}`}>{tip.length - (cnt-1)} more...</div>);
						break;
					}
				}

				return <div>{content}</div>;
			} else {
				return <div>{tip}</div>;
			}
		}

		return " "; // because Tooltip requires a title
	};

	const getWholeTooltip = (tip) => {
		if (!props.disableTooltips) {
			if (Array.isArray(tip)) {
				let user,
					cnt = 1,
					content = [];

				for(var item of tip) {
					user = item.user || item.sender || {name: item.user_name};
					content.push(<div key={`key-${cnt}`}>{user.name}</div>);
					cnt++;
				}

				return <div>{content}</div>;
			} else {
				return <div>{tip}</div>;
			}
		}

		return " "; // because Tooltip requires a title
	};

	const getValue = (value) => {
		if (Array.isArray(value)) {
			return value.length;
		}

		return value;
	};

	let classes = styles(),
		{clickHandler,
			removed, saved, shared, commented, viewed, rated, userViewed, userRated, statusChanged,
			disableTooltips, className, tightPack, densePack, noWrap} = props,
		itemClassName = `${classes.item} ${tightPack ? classes.tightPackItem : densePack ? classes.densePackItem : ''}`,
		removedValue = getValue(removed),
		commentedValue = getValue(commented),
		savedValue = getValue(saved),
		sharedValue = getValue(shared),
		viewedValue = getValue(viewed),
		ratedValue = getValue(rated),
		userViewedValue = getValue(userViewed),
		userRatedValue = getValue(userRated),
		statusChangedValue = getValue(statusChanged);

	return (
		<Grid container
			justify="flex-start"
			wrap={noWrap ? "nowrap" : "wrap"}
			onClick={clickHandler}
			className={`${classes.container} ${className} ${clickHandler ? classes.pointer : ''}`}>
			{statusChangedValue ?
				<Tooltip
					placement="top"
					title={getTooltip(statusChanged)}
					classes={{tooltip: classes.tooltip}}
					disableFocusListener={disableTooltips}
					disableHoverListener={disableTooltips}
					disableTouchListener={disableTooltips}>
					<Grid item className={itemClassName}>
						<span className={classes.circle}>{getIcon(Constants.ACTIVITY.AUDIT_TRAIL.STATUS_CHANGE.ID, theme)}</span>
						<span className={classes.number}>{NumberFormatter.largeNumberFormat(statusChangedValue)}</span>
					</Grid>
				</Tooltip> : null
			}
			{removedValue ?
				<Tooltip
					placement="top"
					title={getTooltip(removed)}
					classes={{tooltip: classes.tooltip}}
					disableFocusListener={disableTooltips}
					disableHoverListener={disableTooltips}
					disableTouchListener={disableTooltips}>
					<Grid item className={itemClassName}>
						<span className={classes.circle}>{getIcon(Constants.ACTIVITY.AUDIT_TRAIL.REMOVE.ID, theme)}</span>
						<span className={classes.number}>{NumberFormatter.largeNumberFormat(removedValue)}</span>
					</Grid>
				</Tooltip> : null
			}
			{commentedValue ?
				<Tooltip
					placement="top"
					title={getTooltip(commented)}
					classes={{tooltip: classes.tooltip}}
					disableFocusListener={disableTooltips}
					disableHoverListener={disableTooltips}
					disableTouchListener={disableTooltips}>
					<Grid item className={itemClassName}>
						<span className={classes.circle}>{getIcon(Constants.ACTIVITY.AUDIT_TRAIL.COMMENT.ID, theme)}</span>
						<span className={classes.number}>{NumberFormatter.largeNumberFormat(commentedValue)}</span>
					</Grid>
				</Tooltip> : null
			}
			{savedValue ?
				<Tooltip
					placement="top"
					title={getTooltip(saved)}
					classes={{tooltip: classes.tooltip}}
					disableFocusListener={disableTooltips}
					disableHoverListener={disableTooltips}
					disableTouchListener={disableTooltips}>
					<Grid item className={itemClassName}>
						<span className={classes.circle}>{getIcon(Constants.ACTIVITY.AUDIT_TRAIL.SAVE.ID, theme)}</span>
						<span className={classes.number}>{NumberFormatter.largeNumberFormat(savedValue)}</span>
					</Grid>
				</Tooltip> : null
			}
			{sharedValue ?
				<Tooltip
					placement="top"
					title={getTooltip(shared)}
					classes={{tooltip: classes.tooltip}}
					disableFocusListener={disableTooltips}
					disableHoverListener={disableTooltips}
					disableTouchListener={disableTooltips}>
					<Grid item className={itemClassName}>
						<span className={classes.circle}>{getIcon(Constants.ACTIVITY.AUDIT_TRAIL.SHARE.ID, theme)}</span>
						<span className={classes.number}>{NumberFormatter.largeNumberFormat(sharedValue)}</span>
					</Grid>
				</Tooltip> : null
			}
			{viewedValue ?
				<Tooltip
					placement="top"
					title={getWholeTooltip(viewed)}
					classes={{tooltip: classes.tooltip}}
					disableFocusListener={disableTooltips}
					disableHoverListener={disableTooltips}
					disableTouchListener={disableTooltips}>
					<Grid item className={itemClassName}>
						<span className={classes.circle}>{getIcon(Constants.ACTIVITY.AUDIT_TRAIL.VIEW.ID, theme)}</span>
						<span className={classes.number}>{NumberFormatter.largeNumberFormat(viewedValue)}</span>
					</Grid>
				</Tooltip> : null
			}
			{ratedValue ?
				<Tooltip
					placement="top"
					title={getTooltip(rated)}
					classes={{tooltip: classes.tooltip}}
					disableFocusListener={disableTooltips}
					disableHoverListener={disableTooltips}
					disableTouchListener={disableTooltips}>
					<Grid item className={itemClassName}>
						<span className={classes.circle}>{getIcon(Constants.ACTIVITY.AUDIT_TRAIL.RATE.ID, theme)}</span>
						<span className={classes.number}>{NumberFormatter.largeNumberFormat(ratedValue)}</span>
					</Grid>
				</Tooltip> : null
			}
			{/*{ratedValue || viewedValue ?
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<Typography className={classes.textIndicator}>
						{viewedValue ? (
							<Tooltip
								placement="top"
								title={getTooltip(viewed)}
								classes={{tooltip: classes.tooltip}}
								disableFocusListener={disableTooltips}
								disableHoverListener={disableTooltips}
								disableTouchListener={disableTooltips}>
								<span>{viewedValue} View{viewed.length > 1 ? "s" : ""}</span>
							</Tooltip>
						) : null}
						{ratedValue ? `${viewedValue ? "  |  " : ""}${ratedValue} Rating${rated.length > 1 ? "s" : ""}` : ""}
					</Typography>
				</Grid> : null
			}*/}
			{userViewedValue || userRatedValue ?
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<Typography className={classes.textIndicator}>
						{userViewedValue ? (
							`${userViewedValue} Alert View${userViewedValue > 1 ? "s" : ""}`
						) : null}
						{userRatedValue ? `${userViewedValue ? "  |  " : ""}${userRatedValue} Rating${userRatedValue > 1 ? "s" : ""}` : ""}
					</Typography>
				</Grid>	: null
			}
		</Grid>
	);
}

UserActivityIndicators.defaultProps = {
	disableTooltips: false,
	noWrap: false
};

UserActivityIndicators.propTypes = {
	clickHandler: PropTypes.func,
	removed: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	saved:  PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	shared:  PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	commented:  PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	viewed:  PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	rated:  PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	userViewed:  PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	userRated:  PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	statusChanged:  PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
	disableTooltips: PropTypes.bool,
	className: PropTypes.string,
	tightPack: PropTypes.bool,
	densePack: PropTypes.bool,
	noWrap: PropTypes.bool
};

export default UserActivityIndicators;
